import PageShell from "@/components/PageShell";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Award, ClipboardCheck, Lock, Users2, Lightbulb, MapPin, Sprout,
  Scale, Briefcase, Mountain, HardHat, TrendingUp, Users, Fuel, ArrowUpRight,
} from "lucide-react";
import { SUBSIDIARIES, VALUES, COMPANY, APPROACH } from "@/lib/content";
import { subsidiaryLogo } from "@/lib/assets";

const VALUE_ICONS = { ShieldCheck, Award, ClipboardCheck, Lock, Users2, Lightbulb, MapPin, Sprout };
const SUB_ICONS = { Scale, Briefcase, Mountain, HardHat, TrendingUp, Users, Fuel };

export default function About() {
  return (
    <PageShell
      testId="about-page"
      title="About Salihi Group — Integrated Professional Services in Somalia"
      description="Salihi Group is a diversified professional services group established 2020 in Mogadishu, Somalia — seven specialised companies delivering legal, consulting, engineering, investment, mining, Oil & Gas, and workforce services."
      eyebrow="About Us"
      heading={<>About <em className="text-gold not-italic">Salihi Group</em>.</>}
      subheading="One name. Seven specialisations. A single standard of professional service."
      breadcrumbs={[{ label: "About" }]}
    >
      {/* ============= OUR STORY ============= */}
      <SectionHeader eyebrow="Our Story" heading="Where we come from." />
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 mb-24">
        <div className="lg:col-span-7 space-y-6 text-navy text-[17px] leading-[1.75] font-light">
          <p>
            Salihi Group was established in <strong className="font-normal">{COMPANY.established}</strong>,
            headquartered in Mogadishu, Somalia. The Group brings together seven specialised subsidiaries
            operating across legal, consulting, engineering, investment, mining, Oil & Gas, and workforce services.
          </p>
          <p>
            The Group was set up to give clients access to the full breadth of professional support they need
            through a single, coordinated relationship — with consistent standards for quality, confidentiality,
            and delivery discipline across every subsidiary. Its foundation combines local familiarity with the
            Somalia market and an internationally informed approach to how professional work is scoped, documented,
            and delivered.
          </p>
        </div>
        <aside className="lg:col-span-5">
          <div className="bg-offwhite border-l-2 border-gold p-8 md:p-10">
            <div className="text-gold text-[11px] uppercase tracking-[0.28em] font-medium mb-4">At a Glance</div>
            <dl className="space-y-4 text-navy text-sm">
              <StatRow label="Established" value={String(COMPANY.established)} />
              <StatRow label="Headquarters" value="Mogadishu, Somalia" />
              <StatRow label="Subsidiaries" value="Seven specialised companies" />
              <StatRow label="Client sectors" value="Twelve sectors served" />
              <StatRow label="Tagline" value={<em className="text-gold not-italic font-serif">{COMPANY.tagline}</em>} />
            </dl>
          </div>
        </aside>
      </div>

      {/* ============= OUR PURPOSE ============= */}
      <SectionHeader eyebrow="Our Purpose" heading="Why the Group exists." />
      <div className="max-w-4xl mb-24">
        <p className="text-navy text-[17px] leading-[1.75] font-light">
          {COMPANY.purpose} The Group&apos;s structure was chosen deliberately: it lets each subsidiary maintain the
          professional independence its discipline requires, while operating under one governance framework and one
          identity in front of clients.
        </p>
      </div>

      {/* ============= VISION & MISSION ============= */}
      <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-24">
        <article className="bg-white border border-softgray p-8 md:p-10" data-testid="about-vision-card">
          <div className="text-gold text-[11px] uppercase tracking-[0.28em] font-medium mb-4">Our Vision</div>
          <p className="text-navy text-[17px] leading-relaxed font-light">{COMPANY.vision}</p>
        </article>
        <article className="bg-navy text-white p-8 md:p-10" data-testid="about-mission-card">
          <div className="text-gold text-[11px] uppercase tracking-[0.28em] font-medium mb-4">Our Mission</div>
          <p className="text-white/85 text-[17px] leading-relaxed font-light">{COMPANY.mission}</p>
        </article>
      </div>

      {/* ============= 8 CORE VALUES ============= */}
      <SectionHeader eyebrow="Our Core Values" heading="The eight values that guide how we work." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-24">
        {VALUES.map((v, i) => {
          const Icon = VALUE_ICONS[v.icon] || ShieldCheck;
          return (
            <article
              key={v.title}
              data-testid={`about-value-${i}`}
              className="bg-white border border-softgray p-6 hover:border-gold transition-colors"
            >
              <Icon size={22} strokeWidth={1.5} className="text-gold mb-5" />
              <div className="text-mutedink text-[10px] uppercase tracking-widest font-mono mb-2">0{i + 1}</div>
              <h3 className="font-serif text-navy text-lg mb-2 leading-tight">{v.title}</h3>
              <p className="text-mutedink text-[14px] leading-relaxed font-light">{v.body}</p>
            </article>
          );
        })}
      </div>

      {/* ============= GROUP STRUCTURE ============= */}
      <SectionHeader eyebrow="Group Structure" heading="One holding company. Seven specialised subsidiaries." />
      <div className="grid lg:grid-cols-12 gap-12 mb-24">
        <div className="lg:col-span-5 space-y-6 text-navy text-[17px] leading-[1.75] font-light">
          <p>
            Salihi Group operates as the parent holding company overseeing seven specialised subsidiaries.
            Each subsidiary is set up as a professional practice in its own discipline — with the independence
            its work requires — while operating within the Group&apos;s shared governance, quality, and confidentiality
            standards.
          </p>
          <p>
            Clients typically engage a single subsidiary, or several coordinated across the Group. In either case,
            the client experience is designed to be coherent: one identity, one point of contact, and one standard
            of delivery.
          </p>
        </div>
        <div className="lg:col-span-7">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {SUBSIDIARIES.map((s) => {
              const Icon = SUB_ICONS[s.icon] || Briefcase;
              return (
                <Link
                  key={s.slug}
                  to={`/companies/${s.slug}`}
                  data-testid={`about-structure-${s.slug}`}
                  className="group flex flex-col items-center text-center p-5 border border-softgray hover:border-gold hover:bg-offwhite transition-colors"
                >
                  <img src={subsidiaryLogo(s)} alt={`${s.name} logo`}
                       className="h-11 w-auto object-contain mb-3" loading="lazy" />
                  <div className="text-navy font-serif text-[13px] leading-tight mb-1">{s.name}</div>
                  <div className="text-mutedink text-[10px] uppercase tracking-widest flex items-center gap-1">
                    <Icon size={11} strokeWidth={1.5} /> {s.tagline}
                  </div>
                  <ArrowUpRight size={12} className="text-mutedink group-hover:text-gold mt-3" />
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* ============= COMMITMENT TO CLIENTS ============= */}
      <SectionHeader eyebrow="Our Commitment" heading="What clients can expect from Salihi Group." />
      <div className="grid md:grid-cols-2 gap-x-12 gap-y-4 mb-24">
        {[
          "A relationship approach — we prioritise long-term client relationships over one-off transactions.",
          "Responsible growth — advice grounded in what is commercially sensible, not what is theoretically ideal.",
          "Confidentiality — controlled information handling, at systems, personnel, and process levels.",
          "Quality — deliverables reviewed to Group quality standards before they leave the firm.",
          "Accountability — clear ownership of every engagement, senior oversight on outcomes.",
        ].map((line, i) => (
          <div key={i} className="flex gap-4 items-start border-b border-softgray py-4" data-testid={`about-commitment-${i}`}>
            <span className="font-mono text-gold text-sm tabular-nums pt-1">0{i + 1}</span>
            <span className="text-navy text-[16px] font-light leading-relaxed">{line}</span>
          </div>
        ))}
      </div>

      {/* ============= INTERNATIONAL STANDARDS + LOCAL KNOWLEDGE ============= */}
      <div className="grid md:grid-cols-2 gap-8 mb-24">
        <article className="p-8 md:p-10 border border-softgray">
          <div className="text-gold text-[11px] uppercase tracking-[0.28em] font-medium mb-3">International Standards</div>
          <h3 className="font-serif text-navy text-2xl mb-4">Documented process. Professional discipline.</h3>
          <p className="text-mutedink text-[15px] leading-relaxed font-light">
            We work from clear scope and terms, produce documented advice, apply quality reviews, and communicate
            in a manner appropriate to serious professional engagements. These are habits, not decorations.
          </p>
        </article>
        <article className="p-8 md:p-10 border border-softgray">
          <div className="text-gold text-[11px] uppercase tracking-[0.28em] font-medium mb-3">Local Knowledge</div>
          <h3 className="font-serif text-navy text-2xl mb-4">Rooted in Mogadishu.</h3>
          <p className="text-mutedink text-[15px] leading-relaxed font-light">
            Salihi Group is a Somalia-based platform. Our people understand how business is actually conducted,
            coordinated, and resolved in this market — and how to translate that reality for international clients
            and partners.
          </p>
        </article>
      </div>

      {/* ============= APPROACH RECAP ============= */}
      <SectionHeader eyebrow="Our Approach" heading="A consistent process across every engagement." />
      <ol className="grid md:grid-cols-2 gap-x-10 gap-y-6 text-navy">
        {APPROACH.map((step, i) => (
          <li key={step} className="flex gap-5 items-start border-b border-softgray pb-6">
            <span className="font-mono text-gold text-sm tabular-nums pt-1">0{i + 1}</span>
            <span className="text-[16px] font-light leading-relaxed">{step}</span>
          </li>
        ))}
      </ol>

      {/* CTA */}
      <div className="mt-24 border-t-2 border-gold pt-14 text-center">
        <h3 className="font-serif text-navy text-3xl md:text-4xl font-light mb-4">Ready to start a conversation?</h3>
        <p className="text-mutedink max-w-2xl mx-auto mb-8">
          Whether you have a specific mandate in mind or want to explore what Salihi Group can offer, we welcome the enquiry.
        </p>
        <Link to="/contact" className="btn-gold" data-testid="about-cta-contact">
          Contact Salihi Group <ArrowUpRight size={14} />
        </Link>
      </div>
    </PageShell>
  );
}

function SectionHeader({ eyebrow, heading }) {
  return (
    <div className="max-w-3xl mb-10">
      <div className="flex items-center gap-3 mb-4">
        <span className="gold-hairline" />
        <span className="text-gold text-[11px] uppercase tracking-[0.28em] font-medium">{eyebrow}</span>
      </div>
      <h2 className="font-serif text-navy text-3xl md:text-4xl font-light">{heading}</h2>
    </div>
  );
}

function StatRow({ label, value }) {
  return (
    <div>
      <dt className="text-mutedink text-xs uppercase tracking-wider mb-1">{label}</dt>
      <dd className="text-navy">{value}</dd>
    </div>
  );
}
